import React, { createContext, useContext, useState } from 'react';
import { cancelStreakReminder } from '../utils/notifications';

const AuthContext = createContext();

const STORAGE_KEY = 'bibleapp_auth';
const API_BASE = (process.env.REACT_APP_API_URL || 'https://bibleduo-game-production.up.railway.app').replace(/\/$/, '');

// Wrapper fetch qui déclenche un événement global sur 401 (token expiré ou invalide).
async function apiFetch(url, options) {
  const res = await fetch(url, options);
  if (res.status === 401) {
    window.dispatchEvent(new Event('auth:unauthorized'));
  }
  return res;
}

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { user: null, token: null };
  } catch {
    return { user: null, token: null };
  }
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(readStorage);

  const persist = (data) => {
    setAuth(data);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  };

  const logout = () => {
    setAuth({ user: null, token: null });
    localStorage.removeItem(STORAGE_KEY);
    // fix : annule le rappel local de streak (y compris sur logout auto après 401)
    cancelStreakReminder().catch(() => {});
  };

  // Écoute les 401 émis par apiFetch → logout automatique (token expiré)
  React.useEffect(() => {
    window.addEventListener('auth:unauthorized', logout);
    return () => window.removeEventListener('auth:unauthorized', logout);
  }, []); // eslint-disable-line

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Connexion échouée.');
    persist({ user: data.user, token: data.token });
  };

  const register = async (name, email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Inscription échouée.');
    // register retourne seulement { token } — on enchaîne un login pour récupérer le profil
    await login(email, password);
  };

  const loginBIP39 = async (name, mnemonic) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, mnemonic }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Connexion BIP39 échouée.');
    persist({ user: data.user, token: data.token });
  };

  // Inscrit le compte sans le connecter (la connexion se fait après affichage de la phrase)
  const registerBIP39 = async (name, mnemonic) => {
    const res = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, mnemonic }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Inscription BIP39 échouée.');
  };

  // Suppression définitive du compte de l'utilisateur connecté.
  // Le nettoyage local n'a lieu qu'APRÈS un succès API.
  const deleteAccount = async () => {
    const token = auth.token;
    if (!token) throw new Error('Non authentifié.');

    const res = await apiFetch(`${API_BASE}/api/users/me`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Suppression du compte échouée.');

    logout();
    // fix : suppression ciblée des seules clés Manna (jamais localStorage.clear())
    ['bibleapp_stats', 'bibleapp_profile', 'bibleapp_escape_badges', 'bibleapp_best_scores']
      .forEach((key) => localStorage.removeItem(key));
  };

  return (
    <AuthContext.Provider value={{
      user: auth.user,
      token: auth.token,
      isAuthenticated: !!auth.token,
      login,
      register,
      loginBIP39,
      registerBIP39,
      deleteAccount,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
